import { QueryClient } from 'react-query';
import { Testlet, TestletSession, TestletSessionActivity, TestSession } from '../types';

export const updateCachedData = <T extends { id: number }>(
  queryClient: QueryClient,
  queryKey: (string | number)[],
  updatedItem: T | T[],
) => {
  const cachedData = queryClient.getQueryData<T[]>(queryKey);
  if (!cachedData) {
    return;
  }

  if (Array.isArray(updatedItem)) {
    const updatedData = cachedData.map((item) => {
      const relatedItem = updatedItem.find((i) => i.id === item.id);
      if (!relatedItem) {
        return item;
      }
      return {
        ...item,
        ...relatedItem,
      };
    });
    queryClient.setQueryData(queryKey, updatedData);
  } else {
    const updatedData = cachedData.map((item) => {
      if (item.id === updatedItem.id) {
        return {
          ...item,
          ...updatedItem,
        };
      }
      return item;
    });
    queryClient.setQueryData(queryKey, updatedData);
  }
};

// Helper function to recursively update nested objects
const updateNestedObject = <T extends Record<string, any> | Array<Record<string, any>>>(
  sourceData: T,
  pathParts: string[],
  updatedData: Partial<T>,
): any => {
  if (pathParts.length === 0) {
    return { ...sourceData, ...updatedData };
  }

  const [currentPart, ...remainingParts] = pathParts;

  // Check if current part is an ID wrapped in brackets [id]
  const idMatch = currentPart.match(/^\[(\d+)\]$/);

  if (idMatch) {
    // Current part is an ID in brackets, so we're looking for an object in an array
    const id = parseInt(idMatch[1], 10);

    // Search for the item with this ID in the current array
    const itemIndex = sourceData.findIndex((item: any) => item.id === id);

    if (itemIndex !== -1) {
      // Found the item, update it
      const updatedArray = [...(sourceData as Array<Record<string, any>>)];

      updatedArray[itemIndex] = updateNestedObject(
        sourceData[itemIndex],
        remainingParts,
        updatedData,
      );

      return updatedArray;
    }

    // If not found, return original array
    return sourceData;
  }

  // Current part is a property name
  if (sourceData[currentPart] && remainingParts.length > 0) {
    return {
      ...sourceData,
      [currentPart]: updateNestedObject(
        sourceData[currentPart],
        remainingParts,
        updatedData,
      ),
    };
  }

  if (remainingParts.length === 0) {
    return {
      ...sourceData,
      [currentPart]: { ...sourceData[currentPart], ...updatedData },
    };
  }

  return sourceData;
};

/**
 * Deep nested update function that can handle paths like [id].testlets.[id]
 * @param queryClient - The query client to update
 * @param queryKey - The query key to update
 * @param path - The path to update use [] to wrap the id on the array of objects and . to access the property of the object . note : don't put multiple [] in a row
 * @param updatedData - The data to update
 */
export const updateDeepNestedCachedData = <T extends { id: number }>(
  queryClient: QueryClient,
  queryKey: (string | number | Record<string, any>)[],
  path: string, // e.g., "157677.testlets.[986]" or "[157677].exam.sections.[17174]"
  updatedData: Partial<T>,
) => {
  const cachedData = queryClient.getQueryData<T>(queryKey);
  if (!cachedData) {
    return;
  }

  // Parse the path to get the IDs and property names
  const updatedCachedData = updateNestedObject<T>(
    cachedData,
    path.split('.'),
    updatedData,
  );

  queryClient.setQueryData(queryKey, updatedCachedData);
};

export const updateTestletInLearnosityExamTestSessions = (
  queryClient: QueryClient,
  updatedTestlet: Testlet,
  testSessionId: number,
  sectionId: number,
) => {
  updateDeepNestedCachedData(
    queryClient,
    ['LE', 'testSessions', sectionId],
    `[${testSessionId}].testlets.[${updatedTestlet.id}]`,
    updatedTestlet,
  );
};
export const updateTestSessionInLearnosityExamTestSessions = (
  queryClient: QueryClient,
  updatedTestSession: TestSession,
  testSessionId: number,
  sectionId: number,
) => {
  updateDeepNestedCachedData(
    queryClient,
    ['LE', 'testSessions', sectionId],
    `[${testSessionId}]`,
    updatedTestSession,
  );
};

export const updateCachedTestlet = (
  queryClient: QueryClient,
  updatedTestlet: Testlet,
  testSessionId: number,
) => {
  updateCachedData(queryClient, ['testlets', +testSessionId], updatedTestlet);
};

export const updateCachedTestletSession = (
  queryClient: QueryClient,
  updatedTestletSession:
    | (Partial<TestletSession> & { id: number })
    | (Partial<TestletSession> & { id: number }[]),
  testletId: number,
) => {
  updateCachedData(queryClient, ['testletSessions', +testletId], updatedTestletSession);
};

export const updateCachedTestletSessionActivity = (
  queryClient: QueryClient,
  newTestletSessionActivity: TestletSessionActivity,
  testletSessionId: number,
) => {
  // @todo
  let cachedData = queryClient.getQueryData<TestletSessionActivity[]>([
    'testletSessionActivities',
    testletSessionId,
  ]);
  // if is undefined, set as empty array
  if (!cachedData) {
    cachedData = [];
  }
  cachedData.push(newTestletSessionActivity);
  queryClient.setQueryData(['testletSessionActivities', testletSessionId], cachedData);
};
